import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../api/userApi';

// Public page reached via the link in the reset-password email.
// Reads ?token=... from the URL, asks for a new password + confirmation,
// posts to AuthController.ResetPassword. On success → redirects to /login
// after a brief confirmation.
export default function ResetPassword() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const [pw, setPw] = useState('');
    const [pw2, setPw2] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) setError('Missing reset token. Please use the link from your email.');
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (pw.length < 6) { setError('Password must be at least 6 characters.'); return; }
        if (pw !== pw2)    { setError('Passwords do not match.'); return; }
        setLoading(true);
        try {
            const res = await resetPassword(token, pw);
            if (res?.data?.ok) {
                setDone(true);
                // Auto-redirect after 3 seconds so the user lands on login
                setTimeout(() => navigate('/'), 3000);
            } else {
                setError(res?.data?.message || 'Could not reset password.');
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data || err?.message;
            setError(typeof msg === 'string' ? msg : 'Could not reset password. The link may have expired.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: '#F8FAFC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
        }}>
            <div style={{
                width: '100%',
                maxWidth: 420,
                borderRadius: 14,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                padding: '40px 32px',
                background: 'white',
                boxSizing: 'border-box',
            }}>
                <img src="/nexwaveLogo.png" alt="Nexwave" style={{ height: 64, display: 'block', margin: '0 auto 12px' }} />
                <div style={{ textAlign: 'center', fontFamily: 'Poppins', fontWeight: 700, fontSize: 24, color: '#0F172A', marginBottom: 4 }}>
                    Choose a new password
                </div>
                <div style={{ textAlign: 'center', fontSize: 14, color: '#64748B', fontWeight: 400, fontFamily: 'Poppins', marginBottom: 32 }}>
                    Reset links expire 1 hour after they're sent.
                </div>

                {done ? (
                    <div style={{
                        background: '#ECFDF5',
                        border: '1px solid #A7F3D0',
                        borderRadius: 8,
                        padding: 16,
                        color: '#065F46',
                        fontSize: 14,
                        fontFamily: 'Poppins',
                        lineHeight: 1.5,
                        marginBottom: 16,
                    }}>
                        Password updated. Redirecting you to the login page…
                    </div>
                ) : (
                    <>
                        {error && (
                            <div style={{
                                background: '#FEF2F2',
                                border: '1px solid #FECACA',
                                borderRadius: 8,
                                padding: 12,
                                color: '#DC2626',
                                fontSize: 13,
                                fontFamily: 'Poppins',
                                marginBottom: 16,
                            }}>
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block', fontFamily: 'Poppins' }}>
                                New password
                            </label>
                            <input
                                type="password"
                                placeholder="At least 6 characters"
                                value={pw}
                                onChange={(e) => setPw(e.target.value)}
                                disabled={!token}
                                required
                                minLength={6}
                                style={{
                                    width: '100%',
                                    padding: '10px 14px',
                                    borderRadius: 8,
                                    border: '1px solid #E2E8F0',
                                    fontSize: 14,
                                    fontFamily: 'Poppins',
                                    marginBottom: 16,
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#1A8CFF'}
                                onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                            />

                            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block', fontFamily: 'Poppins' }}>
                                Confirm new password
                            </label>
                            <input
                                type="password"
                                placeholder="Re-enter the same password"
                                value={pw2}
                                onChange={(e) => setPw2(e.target.value)}
                                disabled={!token}
                                required
                                style={{
                                    width: '100%',
                                    padding: '10px 14px',
                                    borderRadius: 8,
                                    border: '1px solid #E2E8F0',
                                    fontSize: 14,
                                    fontFamily: 'Poppins',
                                    marginBottom: 24,
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#1A8CFF'}
                                onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                            />

                            <button
                                type="submit"
                                disabled={loading || !token}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 8,
                                    fontSize: 15,
                                    fontWeight: 600,
                                    cursor: (loading || !token) ? 'default' : 'pointer',
                                    fontFamily: 'Poppins',
                                    opacity: (loading || !token) ? 0.7 : 1,
                                }}
                            >
                                {loading ? 'Updating…' : 'Update password'}
                            </button>
                        </form>
                    </>
                )}

                <div style={{ textAlign: 'center', marginTop: 24, fontSize: 13, fontFamily: 'Poppins' }}>
                    <Link to="/" style={{ color: '#1A8CFF', fontWeight: 600, textDecoration: 'none' }}>
                        ← Back to login
                    </Link>
                </div>
            </div>
        </div>
    );
}
